import { type FC, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  DegreeCardDetailed,
  Heading,
  Text,
  type DegreeCardDetailedDetailItem,
} from '@highereducation/hen-ui';
import type { Result } from '../../types/result';
import { SUBMITTED_CTA_LABEL } from '../DegreeCardDetailed/useSubmittedForProgram';
import { EventingLocation } from '../../types/enums';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import { useTracking } from '../../providers/TrackingProvider';
import { useIntersect } from '../../hooks/useIntersect';
import { createProductViewedPayload } from '../../utils/createEventingPayloadsDE';
import { shouldShowAccreditationBadge } from '../../utils/accreditationVisibility';
import { createResultsEventContext } from '../../utils/createResultsEventContext';
import tagularEvent from '../../utils/tagularEvent';
import { uuid } from '../../shared/utils';
import { SponsoredResultsPopover } from '../SponsoredResultsPopover';

export interface SpotlightProps {
  results?: Result[];
  firstName?: string;
  productClickedCorrelationId?: string;
  onProgramClick: (result: Result) => void;
  onCloseExperience?: () => void;
  /**
   * Programs the user has already submitted a lead for. When
   * the spotlighted program is among them the card reflects submitted
   * state via the CTA label; conversion still delegates to the governed
   * lead flow (which owns the submitted / duplicate-school rules).
   */
  submittedResults?: { programId?: number; schoolId?: string }[];
  /**
   * Operator-configured modal copy (`resultsPage.spotlight.modal`). When
   * provided these override the default personalized header / subheader
   * so configured copy is what users actually see; otherwise the legacy
   * defaults render.
   */
  title?: string;
  subtitle?: string;
  disclosureTitle?: string;
  disclosureContent?: string;
}

/**
 * Spotlight popup UI rendered inside the Results Page hen-ui Modal.
 *
 * Replaces the legacy se-supersonic-ui `SpotlightUI` (Recommender variant).
 * Shows a single school's spotlight placement: personalized greeting,
 * a hen-ui `DegreeCardDetailed` for the program (school header, tags
 * derived from product flags, snippet description, Explore Program CTA),
 * and a side panel with the school highlights pulled from
 * `result.spotlight`.
 *
 * Eventing parity with the legacy implementation:
 * - `ProductLoaded` on mount.
 * - `ProductViewed` once the spotlight section enters the viewport.
 * - `ProductClicked` on Explore Program (then forwards to onProgramClick).
 *
 * The outer Modal chrome (and its close X) is provided by the parent
 * (`ResultsPage/variants/Default.tsx`); this component renders the
 * inner content only, matching the EDUN-11779 Figma markup.
 */
const Spotlight: FC<SpotlightProps> = ({
  results,
  firstName,
  productClickedCorrelationId,
  onProgramClick,
  onCloseExperience,
  submittedResults = [],
  title,
  subtitle,
  disclosureTitle,
  disclosureContent,
}) => {
  const result = results?.[0];
  const name = firstName?.trim();
  const visibilityRef = useRef<HTMLElement>(null);
  const hasViewedRef = useRef(false);

  const {
    triggerProductLoadedEvent,
    triggerProductClickedEvent,
    eventContext,
  } = useFlowTrackingEvents();

  // Present only when the spotlight renders on the governed DSL result
  // page (inside `TrackingProvider`); `undefined` on the imperative
  // `ResultsPage` so the emitted product payloads stay byte-identical.
  const stepCorrelation = useTracking()?.stepCorrelation;
  const dslConfigId = stepCorrelation?.dslConfigId;
  const dslConfigVersion = stepCorrelation?.dslConfigVersion;

  const viewCorrelationId = useMemo(() => uuid(), []);

  // Resolve a single stable click-correlation id for this spotlight render.
  // The imperative `ResultsPage` supplies one via the prop; the governed DSL
  // popup path (`DslResultsPagePopups`) omits it, so we mint it here. The SAME
  // id must feed both the `ProductClicked` event context and the result handed
  // to `onProgramClick` — otherwise `LeadFlowProvider` mints its own id and the
  // `ProductClicked` event can't be stitched to the downstream conversion /
  // `FormSubmitted` events.
  const resolvedProductClickedCorrelationId = useMemo(
    () => productClickedCorrelationId || uuid(),
    [productClickedCorrelationId],
  );

  const { setNodes, entries } = useIntersect(0);
  const isInView = entries[0]?.isIntersecting ?? false;

  const resultsEventContext = useMemo(
    () =>
      createResultsEventContext({
        eventContext,
        listId: result?.listId,
        presets: {
          degree: result?.degreeSlug || null,
          category: result?.categorySlug || null,
          subject: result?.subjectSlug || null,
        },
        location: EventingLocation.SPOTLIGHT_POPUP,
        productClickedCorrelationId: resolvedProductClickedCorrelationId,
        viewCorrelationId,
        brand: result?.schoolSlug,
        position: 1,
        capId: result?.capId,
        programId: result?.programId,
        isUserRequested: result?.isUserRequested,
        dslConfigId,
        dslConfigVersion,
      }),
    [
      eventContext,
      result,
      viewCorrelationId,
      resolvedProductClickedCorrelationId,
      dslConfigId,
      dslConfigVersion,
    ],
  );

  useEffect(() => {
    if (visibilityRef.current) {
      setNodes([visibilityRef.current]);
    }
  }, [setNodes]);

  useEffect(() => {
    if (hasViewedRef.current || !isInView) return;
    const payload = createProductViewedPayload(resultsEventContext as any);
    tagularEvent(payload.name, payload.payload as any);
    hasViewedRef.current = true;
    setNodes([]);
  }, [isInView, resultsEventContext, setNodes]);

  useEffect(() => {
    triggerProductLoadedEvent(resultsEventContext as any);
    // ProductLoaded fires once per spotlight render; keep deps empty to
    // mirror legacy behavior (otherwise the eventContext memo identity
    // changes and we'd double-fire).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!result) return null;

  const handleExploreClick = () => {
    triggerProductClickedEvent(resultsEventContext as any);
    onProgramClick({
      ...result,
      productClickedCorrelationId: resolvedProductClickedCorrelationId,
    });
  };

  const isProgramSubmitted = submittedResults.some(
    (submission) => submission.programId === result.programId,
  );
  const ctaLabel = isProgramSubmitted ? SUBMITTED_CTA_LABEL : 'Explore Program';

  const spotlightHeader = result.spotlight?.header;
  const highlights = result.spotlight?.blocks?.[0]?.list ?? [];
  const tags = deriveProgramTags(result);
  const schoolDetails =
    result.city && result.state ? `${result.city}, ${result.state}` : undefined;
  const highlightDetailItems: DegreeCardDetailedDetailItem[] = highlights.map(
    ({ text }) => ({ icon: 'circle-check', label: text }),
  );

  return (
    <section
      ref={visibilityRef}
      className='not-prose flex flex-col gap-lg rounded-lg border border-neutral-200 bg-neutral-50 p-lg'
    >
      <header className='flex flex-col gap-xxs'>
        <Heading variant='h3'>
          {title ?? `Great News${name ? `, ${name}!` : '!'}`}
        </Heading>
        <Text variant='b4'>
          {subtitle ?? "We've found a great school near you!"}
        </Text>
      </header>

      <div className='flex flex-col gap-lg lg:flex-row lg:items-stretch'>
        <div className='flex-1 min-w-0 lg:max-w-md'>
          <DegreeCardDetailed
            schoolLogo={`https://simple-storage-server.highereducation.com/${result.schoolSlug}.png?w=130&h=80`}
            schoolName={result.schoolName}
            schoolDetails={schoolDetails}
            degreeTitle={result.programName}
            tags={tags}
            description={result.snippet ?? ''}
            ctaLabel={ctaLabel}
            onCtaClick={handleExploreClick}
            // compare={false}: comparing from inside a post-submission popup
            // is a confused UX — the user has already picked a school.
            compare={false}
            favorite={false}
            border='stroke'
          />
        </div>

        {(spotlightHeader || highlightDetailItems.length > 0) && (
          <aside className='flex flex-1 min-w-0 flex-col gap-md'>
            {spotlightHeader && (
              <Text variant='b4' color='subtext'>
                {spotlightHeader}
              </Text>
            )}
            {highlightDetailItems.length > 0 && (
              <div className='flex flex-col gap-sm'>
                <Heading variant='h5'>School Highlights</Heading>
                <ul className='flex flex-col gap-xs'>
                  {highlightDetailItems.map((item) => (
                    <li key={item.label} className='flex items-start gap-xxs'>
                      <span
                        aria-hidden
                        className='mt-min shrink-0 text-secondary-700'
                      >
                        &#10004;
                      </span>
                      <Text variant='b4'>{item.label}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        )}
      </div>

      <footer className='flex items-center justify-between'>
        <SponsoredResultsPopover
          position='above'
          spanText='Learn More About Our Partners'
          disclosureTitle={disclosureTitle}
          disclosureContent={disclosureContent}
        />
        {onCloseExperience && (
          <Button
            type='button'
            variant='textOnlyNeutral'
            size='sm'
            iconAfter='chevron-right'
            onClick={onCloseExperience}
          >
            Skip to all results
          </Button>
        )}
      </footer>
    </section>
  );
};

/**
 * Build the small `tags` badge list shown below the program title in
 * `DegreeCardDetailed`. Labels mirror `ProductFlagsDictionary` (the
 * source of truth for the legacy `<ProductFlags />` pills) so any
 * future rename in the dictionary doesn't drift between the spotlight
 * card and the rest of the app. Appends the accreditator when present
 * to keep parity with the trust signals the Spotlight previously
 * rendered as a separate pill row.
 */
function deriveProgramTags(result: Result): string[] {
  const tags: string[] = [];
  if (result.isOnline) tags.push('Online');
  if (!result.isOnline) tags.push('On Campus');
  if (result.isPublic) tags.push('Public');
  if (result.isPrivate) tags.push('Private');
  if (result.notForProfit) tags.push('Nonprofit');
  if (result.forProfit) tags.push('Forprofit');
  if (
    shouldShowAccreditationBadge(result.accreditator, result.institutionalStats)
  ) {
    tags.push('Accredited');
  }
  return tags;
}

export default Spotlight;
